import { QuizConfig } from '@app/models/toc.model';



function clamp(n: number, min: number, max: number): number {

  if (!Number.isFinite(n)) return min;

  return Math.min(max, Math.max(min, Math.round(n)));

}



function readPrefCount(

  prefs: Record<string, unknown> | null | undefined,

  dbKey: string,

  quizKey: string,

): number {

  const raw = prefs?.[dbKey] ?? prefs?.[quizKey];

  return Number(raw) || 0;

}



export function isLessonAssessmentYes(prefs?: Record<string, unknown> | null): boolean {

  return String(prefs?.['lessonAssessment'] || 'NA').toUpperCase() === 'Y';

}



/** Map lessonmaster preference columns (or QuizConfig shape) to quiz config for TOC generation. */

export function quizConfigFromLessonPreferences(

  prefs?: Record<string, unknown> | null,

): Partial<QuizConfig> {

  const assessmentY = isLessonAssessmentYes(prefs);

  const perSection = clamp(readPrefCount(prefs, 'lessonQuizPerSection', 'questionsPerSection'), 0, 3);

  const endCount = clamp(readPrefCount(prefs, 'lessonQuizEndCount', 'endOfLessonCount'), 0, 20);

  const runQuizAi = assessmentY && (perSection > 0 || endCount > 0);

  return {

    lessonAssessment: assessmentY ? 'Y' : 'N',

    enabled: runQuizAi,

    questionsPerSection: assessmentY ? perSection : 0,

    endOfLessonCount: assessmentY ? endCount : 0,

    simpleCount: assessmentY

      ? clamp(readPrefCount(prefs, 'lessonQuizSimpleCount', 'simpleCount'), 0, 20)

      : 0,

    complexCount: assessmentY

      ? clamp(readPrefCount(prefs, 'lessonQuizComplexCount', 'complexCount'), 0, 20)

      : 0,

    appliedFinalCount: assessmentY

      ? clamp(readPrefCount(prefs, 'lessonQuizAppliedFinalCount', 'appliedFinalCount'), 0, 20)

      : 0,

  };

}



export function lessonPreferencesFromQuizConfig(cfg: QuizConfig): Record<string, unknown> {

  const assessmentY = cfg.lessonAssessment === 'Y' || cfg.enabled;

  return {

    lessonAssessment: assessmentY ? 'Y' : 'N',

    lessonQuizPerSection: assessmentY ? cfg.questionsPerSection : 0,

    lessonQuizEndCount: assessmentY ? cfg.endOfLessonCount : 0,

    lessonQuizSimpleCount: assessmentY ? cfg.simpleCount : 0,

    lessonQuizComplexCount: assessmentY ? cfg.complexCount : 0,

    lessonQuizAppliedFinalCount: assessmentY ? cfg.appliedFinalCount : 0,

  };

}



function toQuizConfig(d: Partial<QuizConfig>): QuizConfig {

  const assessmentY = d.lessonAssessment === 'Y' || (!!d.enabled && d.lessonAssessment !== 'N');

  const perSection = clamp(Number(d.questionsPerSection) || 0, 0, 3);

  const endCount = clamp(Number(d.endOfLessonCount) || 0, 0, 20);

  const runQuizAi = assessmentY && (perSection > 0 || endCount > 0);

  return {

    lessonAssessment: assessmentY ? 'Y' : 'N',

    enabled: runQuizAi,

    questionsPerSection: perSection,

    endOfLessonCount: endCount,

    simpleCount: clamp(Number(d.simpleCount) || 0, 0, 20),

    complexCount: clamp(Number(d.complexCount) || 0, 0, 20),

    appliedFinalCount: clamp(Number(d.appliedFinalCount) || 0, 0, 20),

  };

}



function promptCount(

  label: string,

  current: number,

  min: number,

  max: number,

): number | null {

  if (typeof window === 'undefined') return current;

  const raw = window.prompt(`${label} (${min}–${max})`, String(current));

  if (raw === null) return null;

  const n = Number(String(raw).trim());

  if (!Number.isFinite(n)) {

    window.alert(`Enter a number between ${min} and ${max}.`);

    return promptCount(label, current, min, max);

  }

  return clamp(n, min, max);

}



/**

 * When assessment is Y, prompt for all quiz count fields (stored in lessonmaster).

 * Returns null if the user cancels.

 */

export function promptQuizConfig(defaults: Partial<QuizConfig>): QuizConfig | null {

  const fromDb = quizConfigFromLessonPreferences(

    defaults as Record<string, unknown> | undefined,

  );

  const assessmentY =

    defaults?.lessonAssessment === 'Y' ||

    fromDb.lessonAssessment === 'Y' ||

    isLessonAssessmentYes(defaults as Record<string, unknown>);



  if (!assessmentY) {

    return toQuizConfig({ lessonAssessment: 'N', enabled: false, ...fromDb });

  }



  if (typeof window === 'undefined') {

    return toQuizConfig({ lessonAssessment: 'Y', ...fromDb });

  }



  if (

    !window.confirm(

      'This lesson includes assessment (quiz).\n\nEnter how many questions to generate per section, at end of lesson, and the end-quiz mix (simple / complex / applied).',

    )

  ) {

    return null;

  }



  const perSection = promptCount(

    'Questions per section (sections with at least 3 content slides)',

    fromDb.questionsPerSection ?? 0,

    0,

    3,

  );

  if (perSection === null) return null;



  const endCount = promptCount('End-of-lesson quiz questions', fromDb.endOfLessonCount ?? 0, 0, 20);

  if (endCount === null) return null;



  const simpleCount = promptCount(

    'End-lesson simple questions (mix guidance)',

    fromDb.simpleCount ?? 0,

    0,

    20,

  );

  if (simpleCount === null) return null;



  const complexCount = promptCount(

    'End-lesson complex questions (mix guidance)',

    fromDb.complexCount ?? 0,

    0,

    20,

  );

  if (complexCount === null) return null;



  const appliedFinalCount = promptCount(

    'End-lesson applied questions (mix guidance)',

    fromDb.appliedFinalCount ?? 0,

    0,

    20,

  );

  if (appliedFinalCount === null) return null;



  if (perSection === 0 && endCount === 0) {

    window.alert(

      'Set at least one of "questions per section" or "end-of-lesson quiz" to a value greater than 0, or turn off assessment in lesson settings.',

    );

    return null;

  }



  return toQuizConfig({

    lessonAssessment: 'Y',

    questionsPerSection: perSection,

    endOfLessonCount: endCount,

    simpleCount,

    complexCount,

    appliedFinalCount,

  });

}


